import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

type Step = 'intent' | 'join' | 'new-team';

interface PublicTeam {
  id: string;
  name: string;
  sport: string;
  season_year: number;
}

const SPORT_ICONS: Record<string, string> = { baseball: '⚾', softball: '🥎', soccer: '⚽', basketball: '🏀' };

// ── Shared logo header ──────────────────────────────────────────────────────
const LogoHeader = ({ subtitle }: { subtitle: string }) => (
  <div style={{ textAlign: 'center', marginBottom: '28px' }}>
    <div style={{
      width: 68, height: 68, borderRadius: '18px', margin: '0 auto 16px',
      background: 'linear-gradient(135deg, #F97316, #EA580C)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 12px 40px rgba(249,115,22,0.25)',
    }}>
      <svg width="34" height="34" viewBox="0 0 20 20" fill="none">
        <polygon points="6,3 17,10 6,17" fill="white"/>
        <rect x="3" y="3" width="2.5" height="14" rx="1.2" fill="white"/>
      </svg>
    </div>
    <div style={{
      fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: 900,
      letterSpacing: '-0.5px', color: 'var(--ink-1)', marginBottom: '4px',
    }}>
      SLOW RIB
    </div>
    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--sr-amber)', letterSpacing: '2px', textTransform: 'uppercase' }}>
      Sports Streaming
    </div>
    <p style={{ color: 'var(--ink-3)', fontSize: '14px', marginTop: '10px' }}>{subtitle}</p>
  </div>
);

// ── Password fields (shared) ─────────────────────────────────────────────────
interface CredFields {
  username: string; email: string; password: string; confirm: string;
}

const CredentialFields = ({
  form, onChange,
}: { form: CredFields; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }) => (
  <>
    <div className="register-fields">
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label htmlFor="username">Username</label>
        <input id="username" name="username" type="text" className="form-control"
          value={form.username} onChange={onChange} placeholder="Your username" required autoComplete="username" />
      </div>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label htmlFor="email">Email</label>
        <input id="email" name="email" type="email" className="form-control"
          value={form.email} onChange={onChange} placeholder="you@example.com" required
          autoComplete="email" inputMode="email"
          autoCapitalize="none" autoCorrect="off" spellCheck={false} />
      </div>
    </div>
    <div style={{ height: '18px' }} />
    <div className="form-group">
      <label htmlFor="password">Password</label>
      <input id="password" name="password" type="password" className="form-control"
        value={form.password} onChange={onChange} placeholder="Min. 6 characters" required autoComplete="new-password" />
    </div>
    <div className="form-group">
      <label htmlFor="confirm">Confirm Password</label>
      <input id="confirm" name="confirm" type="password" className="form-control"
        value={form.confirm} onChange={onChange} placeholder="••••••••" required autoComplete="new-password" />
    </div>
  </>
);

// ── Main component ────────────────────────────────────────────────────────────
const Register = () => {
  const [step, setStep] = useState<Step>('intent');
  const [form, setForm] = useState<CredFields>({ username: '', email: '', password: '', confirm: '' });
  const [pendingTeamName, setPendingTeamName] = useState('');
  const [fullName, setFullName] = useState('');
  const [requestedRole, setRequestedRole] = useState<'fan' | 'player_parent' | 'coach'>('fan');
  const [availableTeams, setAvailableTeams] = useState<PublicTeam[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setTeamsLoading(true);
    api.get('/teams/public')
      .then(r => setAvailableTeams(r.data))
      .catch(() => {})
      .finally(() => setTeamsLoading(false));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const toggleTeam = (teamId: string) =>
    setSelectedTeamIds(prev => prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]);

  const handleBack = () => { setStep('intent'); setError(''); };

  const handleJoinSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError('');
    if (availableTeams.length > 0 && selectedTeamIds.length === 0) {
      setError('Please select at least one team to join');
      return;
    }
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/register', {
        username: form.username,
        email:    form.email.trim().toLowerCase(),
        password: form.password,
        requested_role: requestedRole === 'fan' ? null : requestedRole,
        team_ids: selectedTeamIds,
      });
      login(res.data.token, res.data.user);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally { setLoading(false); }
  };

  const handleNewTeamSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError('');
    if (!pendingTeamName.trim()) { setError('Please enter a team name'); return; }
    if (!fullName.trim())        { setError('Please enter your full name'); return; }
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/register', {
        username:           form.username,
        email:              form.email.trim().toLowerCase(),
        password:           form.password,
        is_team_manager:    true,
        pending_team_name:  pendingTeamName.trim(),
        full_name:          fullName.trim(),
      });
      login(res.data.token, res.data.user);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally { setLoading(false); }
  };

  const needsApproval = requestedRole === 'player_parent' || requestedRole === 'coach';

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    background: 'var(--bg-page)',
    padding: '20px',
  };

  const cardStyle: React.CSSProperties = {
    width: '100%', maxWidth: '440px',
    background: 'var(--surface)', border: '1px solid var(--hairline)',
    borderRadius: 16, padding: 32,
    boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
  };

  const errorBox = (msg: string) => (
    <div style={{
      background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
      borderRadius: 8, padding: '10px 14px', marginBottom: 16,
      fontSize: 13, color: 'var(--loss)', fontWeight: 500,
    }}>
      {msg}
    </div>
  );

  // ── STEP 1: INTENT ──────────────────────────────────────────────────────────
  if (step === 'intent') {
    return (
      <div style={pageStyle}>
        <LogoHeader subtitle="Create your free account" />

        <div style={{ width: '100%', maxWidth: '440px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button
            type="button"
            onClick={() => setStep('join')}
            style={{
              padding: '22px 24px', borderRadius: 14, cursor: 'pointer',
              border: '2px solid var(--hairline)', background: 'var(--surface)', color: 'var(--ink-1)',
              textAlign: 'left', display: 'flex', alignItems: 'center', gap: 16,
              transition: 'border-color 0.15s, box-shadow 0.15s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#F97316'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 16px rgba(249,115,22,0.15)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--hairline)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)'; }}
          >
            <div style={{
              width: 52, height: 52, borderRadius: 12, flexShrink: 0,
              background: 'rgba(249,115,22,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
            }}>🏆</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 4, color: 'var(--ink-1)' }}>
                Join an Existing Team
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.4 }}>
                Fan, Coach, or Player/Parent — follow a team that's already on Slow Rib
              </div>
            </div>
            <i className="ti ti-chevron-right" style={{ color: 'var(--ink-4)', fontSize: 20, flexShrink: 0 }} />
          </button>

          <button
            type="button"
            onClick={() => setStep('new-team')}
            style={{
              padding: '22px 24px', borderRadius: 14, cursor: 'pointer',
              border: '2px solid var(--hairline)', background: 'var(--surface)', color: 'var(--ink-1)',
              textAlign: 'left', display: 'flex', alignItems: 'center', gap: 16,
              transition: 'border-color 0.15s, box-shadow 0.15s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--win)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 16px rgba(34,197,94,0.12)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--hairline)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)'; }}
          >
            <div style={{
              width: 52, height: 52, borderRadius: 12, flexShrink: 0,
              background: 'rgba(34,197,94,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
            }}>🗂️</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 4, color: 'var(--ink-1)' }}>
                Start a New Team
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.4 }}>
                Register as a Team Manager — manage roster, schedule, and stream key
              </div>
            </div>
            <i className="ti ti-chevron-right" style={{ color: 'var(--ink-4)', fontSize: 20, flexShrink: 0 }} />
          </button>
        </div>

        <p style={{ textAlign: 'center', marginTop: 28, color: 'var(--ink-3)', fontSize: 14 }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--sr-amber)', fontWeight: 700 }}>Sign in</Link>
        </p>
      </div>
    );
  }

  // ── STEP 2A: JOIN EXISTING TEAM ─────────────────────────────────────────────
  if (step === 'join') {
    return (
      <div style={pageStyle}>
        <LogoHeader subtitle="Join an Existing Team" />

        <div style={{ width: '100%', maxWidth: '440px' }}>
          <button onClick={handleBack} style={{
            background: 'none', border: 'none', color: 'var(--ink-3)', cursor: 'pointer',
            fontSize: 13, marginBottom: 16, padding: 0, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <i className="ti ti-arrow-left" style={{ fontSize: 12 }} /> Back
          </button>

          {/* Team selection */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '0.8px',
              textTransform: 'uppercase', marginBottom: 8 }}>
              Which team(s) are you joining? *
            </div>
            {teamsLoading ? (
              <div style={{
                padding: '12px 14px', borderRadius: 8,
                background: 'var(--surface-muted)', border: '1px solid var(--hairline)',
                fontSize: 13, color: 'var(--ink-3)', textAlign: 'center',
              }}>
                Loading teams…
              </div>
            ) : availableTeams.length === 0 ? (
              <div style={{
                padding: '12px 14px', borderRadius: 8,
                background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)',
                fontSize: 12, color: 'var(--sr-amber)',
              }}>
                No teams have been set up yet. If you're a team manager, use "Start a New Team" instead.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {availableTeams.map(team => {
                  const selected = selectedTeamIds.includes(team.id);
                  return (
                    <button
                      key={team.id}
                      type="button"
                      onClick={() => toggleTeam(team.id)}
                      style={{
                        padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                        border: `2px solid ${selected ? '#F97316' : 'var(--hairline)'}`,
                        background: selected ? 'rgba(249,115,22,0.06)' : 'var(--surface)',
                        color: 'var(--ink-1)', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 8,
                        transition: 'border-color 0.15s, background 0.15s',
                      }}
                    >
                      <span style={{ fontSize: 16, flexShrink: 0 }}>{SPORT_ICONS[team.sport] || '🏅'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ink-1)' }}>{team.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--ink-4)' }}>{team.season_year}</div>
                      </div>
                      {selected && <i className="ti ti-check" style={{ color: '#F97316', fontSize: 14, flexShrink: 0 }} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Role selection */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '0.8px',
              textTransform: 'uppercase', marginBottom: 10 }}>
              I am a…
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {([
                { value: 'fan',           icon: '👁️', label: 'Fan',            desc: 'Watch games & follow stats — instant access' },
                { value: 'player_parent', icon: '⚽', label: 'Player / Parent', desc: 'Linked to a player account — requires Team Manager or Admin approval' },
                { value: 'coach',         icon: '📢', label: 'Coach',           desc: 'Post feedback to player portals — requires Team Manager or Admin approval' },
              ] as const).map(opt => {
                const selected = requestedRole === opt.value;
                return (
                  <button key={opt.value} type="button" onClick={() => setRequestedRole(opt.value)}
                    style={{
                      padding: '13px 16px', borderRadius: 10, cursor: 'pointer',
                      border: `2px solid ${selected ? '#F97316' : 'var(--hairline)'}`,
                      background: selected ? 'rgba(249,115,22,0.06)' : 'var(--surface)',
                      color: 'var(--ink-1)', textAlign: 'left',
                      display: 'flex', alignItems: 'center', gap: 12,
                      transition: 'border-color 0.15s, background 0.15s',
                    }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{opt.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink-1)' }}>{opt.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{opt.desc}</div>
                    </div>
                    {selected && <i className="ti ti-check" style={{ color: '#F97316', fontSize: 18, flexShrink: 0 }} />}
                  </button>
                );
              })}
            </div>

            {needsApproval && (
              <div style={{
                marginTop: 10, padding: '10px 14px', borderRadius: 8,
                background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.2)',
                fontSize: 12, color: '#B45309', display: 'flex', alignItems: 'flex-start', gap: 8,
              }}>
                <i className="ti ti-clock" style={{ flexShrink: 0, marginTop: 1 }} />
                <span>
                  Your account will be reviewed by a Team Manager or Admin. You'll have Fan access while you wait.
                </span>
              </div>
            )}
          </div>

          {/* Credentials card */}
          <div style={cardStyle}>
            <form onSubmit={handleJoinSubmit}>
              <CredentialFields form={form} onChange={handleChange} />
              {error && errorBox(error)}
              <button type="submit" className="btn btn-primary btn-lg"
                style={{ width: '100%', marginTop: 4, fontSize: 15 }}
                disabled={loading || (availableTeams.length > 0 && selectedTeamIds.length === 0)}>
                {loading
                  ? 'Creating Account…'
                  : needsApproval
                    ? 'Request Account'
                    : 'Create Account — Get Instant Access'}
              </button>
            </form>
          </div>
        </div>

        <p style={{ textAlign: 'center', marginTop: 24, color: 'var(--ink-3)', fontSize: 14 }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--sr-amber)', fontWeight: 700 }}>Sign in</Link>
        </p>
      </div>
    );
  }

  // ── STEP 2B: START A NEW TEAM ───────────────────────────────────────────────
  return (
    <div style={pageStyle}>
      <LogoHeader subtitle="Register as a Team Manager" />

      <div style={{ width: '100%', maxWidth: '440px' }}>
        <button onClick={handleBack} style={{
          background: 'none', border: 'none', color: 'var(--ink-3)', cursor: 'pointer',
          fontSize: 13, marginBottom: 16, padding: 0, display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <i className="ti ti-arrow-left" style={{ fontSize: 12 }} /> Back
        </button>

        <div style={{
          marginBottom: 20, padding: '14px 16px', borderRadius: 10,
          background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)',
          fontSize: 13, color: '#15803D', display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>🗂️</span>
          <span>
            As a Team Manager you can manage your team's roster, schedule, and stream settings. Your account will need admin approval before Team Manager features unlock.
          </span>
        </div>

        <div style={cardStyle}>
          <form onSubmit={handleNewTeamSubmit}>
            <div className="form-group">
              <label htmlFor="pendingTeamName">Team Name</label>
              <input
                id="pendingTeamName" type="text" className="form-control"
                value={pendingTeamName} onChange={e => setPendingTeamName(e.target.value)}
                placeholder="e.g. Carmel FC 2013G Gold" required
              />
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 4 }}>
                What is the name of the team you want to register?
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="fullName">Your Full Name</label>
              <input
                id="fullName" type="text" className="form-control"
                value={fullName} onChange={e => setFullName(e.target.value)}
                placeholder="First and last name" required autoComplete="name"
              />
            </div>

            <div style={{ height: 1, background: 'var(--hairline)', margin: '8px 0 20px' }} />

            <CredentialFields form={form} onChange={handleChange} />

            {error && errorBox(error)}

            <button type="submit" className="btn btn-primary btn-lg"
              style={{ width: '100%', marginTop: 4, fontSize: 15, background: 'var(--win)', borderColor: 'var(--win)' }}
              disabled={loading}>
              {loading ? 'Submitting Request…' : 'Request Team Manager Account'}
            </button>
          </form>
        </div>
      </div>

      <p style={{ textAlign: 'center', marginTop: 24, color: 'var(--ink-3)', fontSize: 14 }}>
        Already have an account?{' '}
        <Link to="/login" style={{ color: 'var(--sr-amber)', fontWeight: 700 }}>Sign in</Link>
      </p>
    </div>
  );
};

export default Register;
